import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Add_Task_Action,
  Get_All_Tasks_For_User_Action,
  Get_specific_Task_For_User_Action,
  update_specific_Task_For_User_Action,
} from '../Redux/Actions/Tasks_Action';
import { Notify } from './Notify';
import CreateIcon        from '@mui/icons-material/Create';
import CheckIcon         from '@mui/icons-material/Check';
import EditIcon          from '@mui/icons-material/Edit';
import CancelIcon        from '@mui/icons-material/Cancel';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CheckCircleIcon   from '@mui/icons-material/CheckCircle';
import SmartToyIcon      from '@mui/icons-material/SmartToy';
import ChatIcon          from '@mui/icons-material/Chat';
import SearchIcon        from '@mui/icons-material/Search';
import HandshakeIcon     from '@mui/icons-material/Handshake';
import LayersIcon        from '@mui/icons-material/Layers';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import CloseIcon         from '@mui/icons-material/Close';
import AddIcon           from '@mui/icons-material/Add';
import TuneIcon          from '@mui/icons-material/Tune';

/* ─── Design tokens ─────────────────────────────────────────────── */
const T = {
  bg:       '#0F1117',
  surface:  '#1A1D27',
  surface2: '#22263A',
  border:   'rgba(255,255,255,0.07)',
  accent:   '#4F6EF7',
  accentGlow:'rgba(79,110,247,0.35)',
  success:  '#22C55E',
  warning:  '#F59E0B',
  danger:   '#EF4444',
  text:     '#F1F3FB',
  textMuted:'#8B92A5',
  radius:   '16px',
};

/* ─── Inline styles ─────────────────────────────────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap');

  @keyframes fb-pulse {
    0%,100%{ box-shadow: 0 0 0 0 ${T.accentGlow}; }
    50%    { box-shadow: 0 0 0 14px transparent; }
  }
  @keyframes fb-in {
    from { opacity:0; transform:translateY(16px) scale(0.96); }
    to   { opacity:1; transform:none; }
  }
  @keyframes fb-spin { to { transform:rotate(360deg); } }
  @keyframes fb-shimmer {
    0%   { background-position: -400px 0; }
    100% { background-position: 400px 0; }
  }

  .fb-root { font-family:'Syne',sans-serif; }

  /* Floating trigger */
  .fb-trigger {
    position:fixed; bottom:28px; right:28px; z-index:40000;
    width:56px; height:56px; border-radius:50%;
    background: linear-gradient(135deg,#0D47A1,#0D47A1);
    border:none; cursor:pointer; color:#fff;
    display:flex; align-items:center; justify-content:center;
    box-shadow: 0 8px 32px rgba(79,110,247,0.5);
    animation: fb-pulse 2.5s ease-in-out infinite;
    transition: transform 0.2s;
  }
  .fb-trigger:hover { transform:scale(1.1); }

  /* Overlay backdrop */
  .fb-backdrop {
    position:fixed; inset:0; z-index:39990;
    background:rgba(0,0,0,0.65);
    backdrop-filter:blur(8px);
    animation:fb-in 0.2s ease both;
  }

  /* Panel */
  .fb-panel {
    position:fixed; z-index:40001;
    background:${T.bg};
    border:1px solid ${T.border};
    border-radius:${T.radius};
    box-shadow: 0 32px 80px rgba(0,0,0,0.6);
    animation:fb-in 0.28s cubic-bezier(0.34,1.2,0.64,1) both;
    display:flex; flex-direction:column; overflow:hidden;
    font-family:'Syne',sans-serif;
  }

  /* Panel header */
  .fb-panel-header {
    display:flex; align-items:center; justify-content:space-between;
    padding:18px 22px 14px;
    border-bottom:1px solid ${T.border};
    flex-shrink:0;
  }
  .fb-panel-title {
    font-size:15px; font-weight:700; color:${T.text};
    letter-spacing:0.02em; margin:0;
  }
  .fb-close {
    width:32px; height:32px; border-radius:8px;
    border:1px solid ${T.border}; background:${T.surface};
    color:${T.textMuted}; cursor:pointer; display:flex;
    align-items:center; justify-content:center;
    transition:all 0.15s;
  }
  .fb-close:hover { background:${T.surface2}; color:${T.text}; }

  /* Tool grid */
  .fb-tool-grid {
    display:grid; grid-template-columns:repeat(3,1fr); gap:10px;
    padding:16px;
  }
  .fb-tool-card {
    display:flex; flex-direction:column; align-items:center;
    gap:8px; padding:16px 10px; border-radius:12px;
    border:1px solid ${T.border}; background:${T.surface};
    cursor:pointer; transition:all 0.18s; user-select:none;
  }
  .fb-tool-card:hover {
    border-color:${T.accent};
    background:${T.surface2};
    transform:translateY(-2px);
    box-shadow:0 8px 24px rgba(79,110,247,0.2);
  }
  .fb-tool-icon {
    width:44px; height:44px; border-radius:12px;
    display:flex; align-items:center; justify-content:center;
    font-size:22px;
  }
  .fb-tool-label {
    font-size:11px; font-weight:600; color:${T.textMuted};
    text-align:center; letter-spacing:0.04em;
  }

  /* Task form */
  .fb-form-body { padding:16px 22px; overflow-y:auto; flex:1; }
  .fb-label {
    font-size:11px; font-weight:700; color:${T.textMuted};
    text-transform:uppercase; letter-spacing:0.08em;
    display:block; margin-bottom:6px;
  }
  .fb-input {
    width:100%; padding:10px 14px;
    background:${T.surface}; border:1px solid ${T.border};
    border-radius:10px; color:${T.text}; font-family:'Syne',sans-serif;
    font-size:13px; outline:none; transition:border-color 0.15s;
    box-sizing:border-box;
  }
  .fb-input:focus { border-color:${T.accent}; }
  .fb-input::placeholder { color:${T.textMuted}; }
  .fb-input-group { margin-bottom:14px; }

  .fb-select {
    width:100%; padding:10px 14px;
    background:${T.surface}; border:1px solid ${T.border};
    border-radius:10px; color:${T.text}; font-family:'Syne',sans-serif;
    font-size:13px; outline:none; cursor:pointer;
    appearance:none; box-sizing:border-box;
  }

  /* Buttons */
  .fb-btn {
    display:inline-flex; align-items:center; gap:6px;
    padding:9px 18px; border-radius:10px; border:none;
    font-family:'Syne',sans-serif; font-size:13px; font-weight:600;
    cursor:pointer; transition:all 0.15s; white-space:nowrap;
  }
  .fb-btn-primary { background:${T.accent}; color:#fff; box-shadow:0 4px 14px rgba(79,110,247,0.35); }
  .fb-btn-primary:hover { background:#3D5CE6; transform:translateY(-1px); }
  .fb-btn-ghost { background:${T.surface}; color:${T.textMuted}; border:1px solid ${T.border}; }
  .fb-btn-ghost:hover { color:${T.text}; border-color:${T.accent}; }
  .fb-btn-danger { background:rgba(239,68,68,0.12); color:${T.danger}; border:1px solid rgba(239,68,68,0.25); }

  /* Task card */
  .fb-task-card {
    background:${T.surface}; border:1px solid ${T.border};
    border-radius:12px; padding:14px 16px; margin-bottom:10px;
    transition:border-color 0.15s;
  }
  .fb-task-card:hover { border-color:rgba(79,110,247,0.3); }
  .fb-task-title { font-size:14px; font-weight:700; color:${T.text}; margin:0 0 4px; }
  .fb-task-desc  { font-size:12px; color:${T.textMuted}; margin:0 0 10px; line-height:1.5; }
  .fb-task-meta  { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
  .fb-badge {
    display:inline-flex; align-items:center; gap:4px;
    padding:3px 10px; border-radius:20px; font-size:11px; font-weight:700;
  }
  .fb-badge-pending   { background:rgba(245,158,11,0.12); color:${T.warning}; }
  .fb-badge-completed { background:rgba(34,197,94,0.12);  color:${T.success}; }
  .fb-badge-cancelled { background:rgba(239,68,68,0.12);  color:${T.danger};  }
  .fb-date { font-size:11px; color:${T.textMuted}; font-family:'DM Mono',monospace; }

  /* Filter panel */
  .fb-filter-row { display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-bottom:12px; }

  /* AI iframe panel */
  .fb-ai-icons {
    display:flex; gap:10px; padding:12px 16px;
    border-bottom:1px solid ${T.border};
    flex-shrink:0; overflow-x:auto;
  }
  .fb-ai-icon-btn {
    display:flex; flex-direction:column; align-items:center; gap:5px;
    padding:8px 12px; border-radius:10px; border:1px solid ${T.border};
    background:${T.surface}; cursor:pointer; transition:all 0.15s;
    flex-shrink:0;
  }
  .fb-ai-icon-btn:hover  { border-color:${T.accent}; background:${T.surface2}; }
  .fb-ai-icon-btn.active { border-color:${T.accent}; background:rgba(79,110,247,0.12); }
  .fb-ai-icon-label { font-size:10px; color:${T.textMuted}; font-weight:600; }

  /* Panel footer */
  .fb-panel-footer {
    padding:12px 22px; border-top:1px solid ${T.border};
    display:flex; gap:8px; flex-shrink:0;
  }

  /* Scrollbar */
  .fb-form-body::-webkit-scrollbar { width:4px; }
  .fb-form-body::-webkit-scrollbar-track { background:transparent; }
  .fb-form-body::-webkit-scrollbar-thumb { background:${T.border}; border-radius:4px; }

  /* Empty state */
  .fb-empty { text-align:center; padding:32px 16px; color:${T.textMuted}; }
  .fb-empty-icon { font-size:40px; margin-bottom:10px; }
  .fb-empty-text { font-size:13px; }

  /* Spinner */
  .fb-spinner {
    width:14px; height:14px; border:2px solid rgba(255,255,255,0.3);
    border-top-color:#fff; border-radius:50%;
    animation:fb-spin 0.7s linear infinite;
  }

  /* divider */
  .fb-divider { height:1px; background:${T.border}; margin:14px 0; }
`;

/* ─── Helpers ─────────────────────────────────────────────────────── */
const statusConfig = {
  pending:   { label:'Pending',   cls:'fb-badge-pending',   dot:'#F59E0B' },
  completed: { label:'Completed', cls:'fb-badge-completed', dot:'#22C55E' },
  cancelled: { label:'Cancelled', cls:'fb-badge-cancelled', dot:'#EF4444' },
};

const aiModels = [
  { key:'chatgpt',  label:'ChatGPT',    icon:<ChatIcon sx={{fontSize:20}}/>,         url:'https://chatgpt.com/',           color:'#10A37F' },
  { key:'deepseek', label:'DeepSeek',   icon:<SearchIcon sx={{fontSize:20}}/>,        url:'https://www.deepseek.com/',      color:'#0066FF' },
  { key:'manus',    label:'Manus',      icon:<HandshakeIcon sx={{fontSize:20}}/>,     url:'https://manus.im/',              color:'#8B5CF6' },
  { key:'cursor',   label:'Cursor AI',  icon:<SpaceDashboardIcon sx={{fontSize:20}}/>,url:'https://cursor.com/agents',     color:'#F97316' },
  { key:'blackbox', label:'Blackbox',   icon:<LayersIcon sx={{fontSize:20}}/>,        url:'https://www.blackbox.ai/',       color:'#EF4444' },
];

/* ─── Sub-components ──────────────────────────────────────────────── */
const FormInput = ({ label, ...props }) => (
  <div className="fb-input-group">
    <label className="fb-label">{label}</label>
    {props.as === 'textarea'
      ? <textarea {...props} className="fb-input" rows={3} style={{resize:'vertical'}} />
      : props.as === 'select'
        ? <select className="fb-select" {...props}>{props.children}</select>
        : <input {...props} className="fb-input" />
    }
  </div>
);

const TaskCard = ({ task, onEdit }) => {
  const cfg = statusConfig[task.status] || statusConfig.pending;
  return (
    <div className="fb-task-card">
      <p className="fb-task-title">{task.title}</p>
      {task.description && <p className="fb-task-desc">{task.description}</p>}
      <div className="fb-task-meta">
        <span className={`fb-badge ${cfg.cls}`}>
          <span style={{width:6,height:6,borderRadius:'50%',background:cfg.dot,display:'inline-block'}}/>
          {cfg.label}
        </span>
        {task.dueDate && (
          <span className="fb-date">
            📅 {new Date(task.dueDate).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'})}
          </span>
        )}
        <button
          className="fb-btn fb-btn-ghost"
          onClick={() => onEdit(task._id)}
          style={{marginLeft:'auto', padding:'5px 12px', fontSize:'12px'}}
        >
          <EditIcon sx={{fontSize:14}}/> Edit
        </button>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════════ */
export const FloatingButton = () => {
  const dispatch = useDispatch();

  // ── view state ─────────────────────────────────────────────────────
  // null | 'menu' | 'tasks' | 'ai' | 'editTask'
  const [view, setView] = useState(null);
  const [iframeUrl, setIframeUrl] = useState('https://www.blackbox.ai/');
  const [activeAI, setActiveAI] = useState('blackbox');
  const [showFilter, setShowFilter] = useState(false);
  const [saving, setSaving] = useState(false);

  // ── user ───────────────────────────────────────────────────────────
  const [userId, setUserId] = useState('');
  useEffect(() => {
    const u = JSON.parse(localStorage.getItem('user'));
    if (u) setUserId(u?.data?._id);
  }, []);

  // ── task form ──────────────────────────────────────────────────────
  const emptyForm = { title:'', description:'', dueDate:'', status:'pending' };
  const [form,    setForm]    = useState(emptyForm);
  const [editId,  setEditId]  = useState(null);

  const setF = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  // ── filter state ───────────────────────────────────────────────────
  const [fromDate,      setFromDate]      = useState('');
  const [toDate,        setToDate]        = useState('');
  const [filterStatus,  setFilterStatus]  = useState('all');

  // ── redux ──────────────────────────────────────────────────────────
  const res_all_tasks = useSelector(s => s.AllUsrsTasks.getTasks);
  const res_one_task  = useSelector(s => s.AllUsrsTasks.getspecificTasks);

  useEffect(() => { if (userId) dispatch(Get_All_Tasks_For_User_Action(userId)); }, [userId]);

  // populate edit form
  useEffect(() => {
    if (res_one_task?.data && view === 'editTask') {
      setForm({
        title:       res_one_task.data.title       || '',
        description: res_one_task.data.description || '',
        dueDate:     res_one_task.data.dueDate
          ? new Date(res_one_task.data.dueDate).toISOString().slice(0,16) : '',
        status:      res_one_task.data.status      || 'pending',
      });
    }
  }, [res_one_task]);

  // ── filtered tasks ─────────────────────────────────────────────────
  const tasks = res_all_tasks?.data || [];
  const filteredTasks = tasks.filter(t => {
    if (filterStatus !== 'all' && t.status !== filterStatus) return false;
    if (fromDate || toDate) {
      const d = t.dueDate ? new Date(t.dueDate).toISOString().slice(0,10) : null;
      if (!d) return false;
      if (fromDate && d < fromDate) return false;
      if (toDate   && d > toDate)   return false;
    }
    return true;
  });
  const hasFilters = filterStatus !== 'all' || fromDate || toDate;
  const displayTasks = hasFilters ? filteredTasks : tasks;

  // ── handlers ───────────────────────────────────────────────────────
  const handleCreateTask = async () => {
    if (!form.title) return Notify('error','Title is required','error');
    setSaving(true);
    await dispatch(Add_Task_Action({ ...form, userId }));
    await dispatch(Get_All_Tasks_For_User_Action(userId));
    setForm(emptyForm);
    setSaving(false);
    Notify('success','Task created!','success');
  };

  const handleEditOpen = async (id) => {
    setEditId(id);
    setView('editTask');
    await dispatch(Get_specific_Task_For_User_Action(id));
  };

  const handleEditSubmit = async () => {
    setSaving(true);
    await dispatch(update_specific_Task_For_User_Action(editId, form));
    await dispatch(Get_All_Tasks_For_User_Action(userId));
    setSaving(false);
    Notify('success','Task updated!','success');
    setView('tasks');
    setEditId(null);
    setForm(emptyForm);
  };

  const handleAI = (model) => {
    if (model.key === 'blackbox') {
      setIframeUrl(model.url); setActiveAI(model.key);
    } else {
      window.open(model.url,'_blank','noopener,noreferrer');
    }
  };

  const close = () => setView(null);

  // ── Panel position (bottom-right) ──────────────────────────────────
  const panelStyle = {
    bottom: 94, right: 28,
    width: view === 'ai' ? 'min(800px, calc(100vw - 56px))' : 'min(460px, calc(100vw - 56px))',
    height: view === 'ai' ? 'min(620px, calc(100vh - 120px))' : 'min(640px, calc(100vh - 120px))',
  };

  /* ── Render ─────────────────────────────────────────────────────── */
  return (
    <>
      <style>{STYLES}</style>

      {/* Backdrop */}
      {view && <div className="fb-backdrop" onClick={close} />}

      {/* Trigger */}
      <button className="fb-trigger" onClick={() => setView(v => v ? null : 'menu')}>
        {view ? <CloseIcon sx={{fontSize:22}}/> : <CreateIcon sx={{fontSize:22}}/>}
      </button>

      {/* Panel */}
      {view && (
        <div className="fb-panel fb-root" style={panelStyle}>

          {/* ── MENU ─────────────────────────────────────────────── */}
          {view === 'menu' && (
            <>
              <div className="fb-panel-header">
                <p className="fb-panel-title">Tools</p>
                <button className="fb-close" onClick={close}><CloseIcon sx={{fontSize:16}}/></button>
              </div>
              <div className="fb-tool-grid">
                {[
                  { label:'Tasks',       icon:'✅', color:'#22C55E', bg:'rgba(34,197,94,0.12)',  action:() => setView('tasks') },
                  { label:'AI Studio',   icon:'🤖', color:'#F97316', bg:'rgba(249,115,22,0.12)', action:() => setView('ai') },
                  { label:'Chat',        icon:'💬', color:'#4F6EF7', bg:'rgba(79,110,247,0.12)', action:() => {} },
                ].map(item => (
                  <div key={item.label} className="fb-tool-card" onClick={item.action}>
                    <div className="fb-tool-icon" style={{background:item.bg, fontSize:24}}>{item.icon}</div>
                    <span className="fb-tool-label">{item.label}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── TASKS ────────────────────────────────────────────── */}
          {(view === 'tasks' || view === 'editTask') && (
            <>
              <div className="fb-panel-header">
                <p className="fb-panel-title">
                  {view === 'editTask' ? '✏️ Edit Task' : '📋 Task Manager'}
                </p>
                <div style={{display:'flex',gap:6}}>
                  {view === 'editTask' && (
                    <button className="fb-close" onClick={() => { setView('tasks'); setForm(emptyForm); }}>
                      ←
                    </button>
                  )}
                  <button className="fb-close" onClick={close}><CloseIcon sx={{fontSize:16}}/></button>
                </div>
              </div>

              <div className="fb-form-body">

                {/* ── Create / Edit form ── */}
                <FormInput label="Title" type="text" placeholder="Task title..." value={form.title} onChange={setF('title')} />
                <FormInput label="Description" as="textarea" placeholder="Optional description..." value={form.description} onChange={setF('description')} />
                <div className="fb-filter-row">
                  <FormInput label="Due Date" type="datetime-local" value={form.dueDate} onChange={setF('dueDate')} />
                  <FormInput label="Status" as="select" value={form.status} onChange={setF('status')}>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </FormInput>
                </div>

                <div style={{display:'flex',gap:8,marginBottom:16}}>
                  <button
                    className="fb-btn fb-btn-primary"
                    onClick={view === 'editTask' ? handleEditSubmit : handleCreateTask}
                    disabled={saving}
                    style={{flex:1,background:'#0D47A1'}}
                  >
                    {saving ? <span className="fb-spinner"/> : <CheckIcon sx={{fontSize:16}}/>}
                    {view === 'editTask' ? 'Save Changes' : 'Create Task'}
                  </button>
                  {view === 'tasks' && (
                    <button
                      className="fb-btn fb-btn-ghost"
                      onClick={() => setShowFilter(v => !v)}
                    >
                      <TuneIcon sx={{fontSize:16}}/>
                      Filter
                    </button>
                  )}
                </div>

                {/* ── Filter ── */}
                {view === 'tasks' && showFilter && (
                  <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:12,padding:14,marginBottom:14}}>
                    <p className="fb-label" style={{marginBottom:10}}>Filter Tasks</p>
                    <div className="fb-filter-row">
                      <FormInput label="From" type="date" value={fromDate} onChange={e=>setFromDate(e.target.value)} />
                      <FormInput label="To"   type="date" value={toDate}   onChange={e=>setToDate(e.target.value)}   />
                    </div>
                    <FormInput label="Status" as="select" value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
                      <option value="all">All</option>
                      <option value="pending">Pending</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </FormInput>
                    <div style={{display:'flex',gap:8}}>
                      <button className="fb-btn fb-btn-ghost" style={{flex:1}} onClick={()=>{setFromDate('');setToDate('');setFilterStatus('all');}}>
                        Reset
                      </button>
                    </div>
                    <p style={{fontSize:11,color:T.textMuted,marginTop:8,marginBottom:0}}>
                      Showing {displayTasks.length} of {tasks.length} tasks
                    </p>
                  </div>
                )}

                {/* ── Tasks list (only on tasks view) ── */}
                {view === 'tasks' && (
                  <>
                    <div className="fb-divider"/>
                    <p className="fb-label">
                      {hasFilters ? `Filtered — ${displayTasks.length} tasks` : `All Tasks — ${tasks.length}`}
                    </p>
                    {displayTasks.length === 0 ? (
                      <div className="fb-empty">
                        <div className="fb-empty-icon">{hasFilters ? '🔍' : '📝'}</div>
                        <p className="fb-empty-text">
                          {hasFilters ? 'No tasks match your filters.' : 'No tasks yet. Create one above!'}
                        </p>
                      </div>
                    ) : (
                      displayTasks.map(task => (
                        <TaskCard key={task._id} task={task} onEdit={handleEditOpen} />
                      ))
                    )}
                  </>
                )}

              </div>
            </>
          )}

          {/* ── AI STUDIO ────────────────────────────────────────── */}
          {view === 'ai' && (
            <>
              <div className="fb-panel-header">
                <p className="fb-panel-title">🤖 AI Studio</p>
                <button className="fb-close" onClick={close}><CloseIcon sx={{fontSize:16}}/></button>
              </div>

              <div className="fb-ai-icons">
                {aiModels.map(m => (
                  <div
                    key={m.key}
                    className={`fb-ai-icon-btn ${activeAI===m.key?'active':''}`}
                    onClick={() => handleAI(m)}
                  >
                    <div style={{color:m.color}}>{m.icon}</div>
                    <span className="fb-ai-icon-label">{m.label}</span>
                  </div>
                ))}
              </div>

              <iframe
                title="AI"
                src={iframeUrl}
                style={{flex:1,border:'none',width:'100%'}}
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              />
            </>
          )}

        </div>
      )}
    </>
  );
};
